import Backbone from 'init/backbone'
import debounce from 'lodash/debounce'
import isNull from 'lodash/isNull'
import moment from 'moment'
import { Defaults } from 'es-decorators'
import { Collection } from 'es6/components/collection'
import { Model } from 'es6/components/model'

/**
 * Коллекция поиска новостей.
 */
@Defaults({
  urlRoot: '/api/v3/news-search',
  requestParameters: `published_after=${moment().subtract(1, 'years').format('YYYY-MM-DD')}`,
  model: Model,
  state: {
    isLastPage: false,
    unusedIndex: 0,
    loadedItems: 0,
    totalRecords: 0,
    page: 0,
  },
  query: '',
})
export class NewsSearch extends Collection {
  /** @inheritDoc */
  run () {
    this.listenTo(this, 'sync', () => {
      this.state.unusedIndex = 0
    })
    this.listenTo(Backbone, 'update-chosen-filters', debounce((index, changedFilters) => {
      this.setCategoryIdAndFetch(changedFilters)
    }, 1000))
  }

  /**
   * Устанавливает categoryId и делает запрос.
   * @param {Collection} changedFilters Измененные фильтры.
   */
  setCategoryIdAndFetch (changedFilters) {
    const categoryFilter = changedFilters.findWhere({ paramName: 'category' })
    const categoryId = categoryFilter?.get('values').filter(val => val.selected)[0]?.id
    if (this.categoryId !== categoryId) {
      this.categoryId = categoryId
      this.freshFetch()
    }
  }

  /**
   * Возвращает данные для запроса к серверу.
   * @returns {Object} Данные для запроса.
   */
  getPayload () {
    return Object.assign({
      q: this.query,
      page: this.state.page + 1,
    }, this.categoryId && {
      category_id: this.categoryId,
    })
  }

  /**
   * Переопределяет массив для добавления моделей в коллекцию.
   * @param {Object} response Ответ с API.
   * @returns {Array} Массив с новостями.
   */
  parse (response) {
    this.parseState(response)
    return response.news
  }

  /**
   * Возвращает признак всех загруженных новостей.
   * @returns boolean Остались новости для загрузки.
   */
  hasMoreNews () {
    return this.state.loadedItems < this.state.totalRecords
  }

  /**
   * Запрос новостей.
   */
  requestMoreNews () {
    this.fetch({ data: this.getPayload() })
  }

  /**
   * Парсит состояние коллекции из ответа.
   * @param {Object} response Ответ сервера.
   */
  parseState (response) {
    this.state = Object.assign({}, this.state, {
      page: response._meta.currentPage,
      isLastPage: response._meta.pageCount === response._meta.currentPage,
      lastPage: response._meta.pageCount,
      totalRecords: response._meta.totalCount,
    }, !isNull(response.news) && {
      loadedItems: this.state.loadedItems + response.news.length,
    })
  }

  /**
   * Возвращает последнюю неиспользованную модель.
   * @returns Model Последняя неиспользованная модель.
   */
  getUnusedModel () {
    const unusedModel = this.at(this.state.unusedIndex)
    this.state.unusedIndex += 1

    return unusedModel
  }

  /**
   * Устанавливает значение поля запроса и обновляет коллекцию.
   * @param {string} query Значение поля поиска.
   */
  setQuery (query) {
    if (query !== this.query) {
      this.query = query
      this.freshFetch()
      this.trigger('change-query')
    }
  }

  /**
   * Делает запрос со сбросом текущего состояния.
   */
  freshFetch () {
    this.state.page = 0
    this.state.unusedIndex = 0
    this.requestMoreNews()
  }
}
